<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	//Stores
	import { user_data } from 'src/stores/account/user';
import { devices } from 'src/stores/lists/devices';
	//Design system components
	import {
		Button,
		Text,
		Container,
		Icon,
		CircularProgress,
		Alert,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../../types/Device';
	import { postReq } from 'src/services/http';
	import { createEventDispatcher } from 'svelte';
	import { capFirstChar } from 'src/helpers/utilities';

	const dispatch = createEventDispatcher();

	export let device: Device,
		timeToReset: number,
		showWarningIncorrectData: boolean = false,
		deviceIsOnline: boolean = false,
		showResetModal = false,
		progressReadinessPercent: number = 0,
		starLearningTs: number = 0,
		lastResetTs: number = 0,
		enabled: boolean = false,
		showAboutAIModelModal = false,
		remainingHours: number = 0,
		requiredHours: number = 0,
		useNewLayout = false;

	//AIModelStartDate is the date when the AI model started learning.
	//If the start date is zero, but there is a timestamp in reset key, the AIModelStartDate is the date when the AI model was
	//last reset. If the AI model is not started yet and was never reset,
	//the AIModelStartDate is an empty string.
	$: AIModelStartDateRaw = new Date(lastResetTs || starLearningTs || Date.now()).toDateString();
	$: AIModelStartDate =
		AIModelStartDateRaw.length !== 0 ? generateDateString(AIModelStartDateRaw) : '';

	function generateDateString(date) {
		//date is a string in format day, month, number of the day and year
		// extract the day, month, and year from the date string
		const [day, month, dayNumber, year] = date.split(' ');
		//return the date in format month, day, year
		let pureMonth = month.toLowerCase();

		return `${$format(`select_option_chart_short_month_${pureMonth}`)} ${dayNumber}, ${year}`;
	}
	$: remainingHours = remainingHours !== 0 ? remainingHours : requiredHours;

	let loading = false;

	//if it is enable (toggle on) then the training is on and on the button should be written "pause". otherwise the training is paused and in the buttons should be written  "start" or "resume"
	//if the AI model is not started yet, the button should be written "start"
	//if the AI model is started but paused, the button should be written "resume"
	$: AIModelState = enabled ? 'pause' : starLearningTs !== 0 ? 'resume' : 'start';

	async function handleAITraining() {
		// Enable AI model and start the learning process from scratch at this point in time.

		const newCheckedValue = enabled;

		loading = true;
		const res = await postReq('interface/device/set_cloud_options', {
			id: device.id,
			set: 'ai_device_failure',
			val: newCheckedValue,
		});
		loading = false;

		if (res?.isok) {
			enabled = newCheckedValue;
			if (!device.cloud_options) {
				device.cloud_options = {};
			}
			device.cloud_options.ai_device_failure = newCheckedValue;
			devices.update(device);
			dispatch('checkAIStatus');
		} else {
			enabled = !newCheckedValue;
		}

		const toastTexts = newCheckedValue
			? {
					success: $format('toast_succ_resume_ai_training', {
						default: 'The AI model resumed training.',
					}),
					danger: $format('setting_err_toast', {
						default: 'Failed to apply settings.',
					}),
				}
			: {
					success: $format('toast_succ_ai_paused', {
						default: 'The AI model is paused.',
					}),
					danger: $format('setting_err_toast', {
						default: 'Failed to apply settings.',
					}),
				};
		onSettingsResponse(res, device, toastTexts);
	}
</script>

<Container backgroundColor={useNewLayout ? 'color-0' : undefined}>
	<div class="content-background">
		<div class="mb-m">
			<div class="mb-s d-flex align-items-center">
				<Icon
					class="mr-s text-primary"
					icon="fas fa-screwdriver-wrench icon-ai-color"
					size="s"
				/>
				<Text element="p" styleType="body-small" class="text-bold">
					{$format('ai_model_training', {
						default: 'AI Model training',
					})}</Text
				>
			</div>
		</div>
		<!-- Alert notifying that resetting process is starting and the data that shows may not be correct -->
		{#if showWarningIncorrectData}
			<Alert
				class="mb-m"
				heading={$format('alert_ai_model_incorrect_data', {
					default: `The AI model is resetting. This will take about ${timeToReset} minutes. While the AI model is resetting, the data will be old or not be correct. Please check back later.`,
				})}
			/>
		{/if}
		<div class="mb-m">
			<!-- 1. AI model progress data in percent
				2. Start date of the AI model
				3. Remaining time to complete the AI model training 
			-->
			<div class="d-flex align-items-center mb-s">
				<div class="mr-s">
					<CircularProgress
						progress={progressReadinessPercent}
						disabled={!enabled || !deviceIsOnline}
						type="ai"
					/>
				</div>
				<div>
					{#if progressReadinessPercent !== 100}
						<div>
							<Text element="p" class="text-color-muted">
								<Text
									element="span"
									styleType="body-extra-small"
									class={`text-bold ${!enabled || !deviceIsOnline ? 'text-color-muted' : ''}`}
								>
									{capFirstChar(
										$format('label_start', {
											default: 'Start',
										}),
									)}:
								</Text>
								<Text
									element="span"
									styleType="body-extra-small"
									class={!enabled ? 'text-color-muted' : ''}
								>
									{AIModelStartDate}
								</Text>
							</Text>
						</div>
						<div>
							{#if !deviceIsOnline}
								<Text element="span" styleType="body-extra-small">
									{$format('device_status_offline', {
										default: 'Device is offline',
									})}
								</Text>
							{:else}
								<Text element="p" class="text-color-muted">
									<Text
										element="span"
										styleType="body-extra-small"
										class={`text-bold ${!enabled ? 'text-color-muted' : ''}`}
									>
										{capFirstChar(
											$format('label_remaining_time', {
												default: 'Remaining time:',
											}),
										)}
									</Text>
									<Text
										element="span"
										styleType="body-extra-small"
										class={!enabled ? 'text-color-muted' : ''}
									>
										{remainingHours}
										{remainingHours === 1 ? 'hour' : 'hours'}
									</Text>
								</Text>
							{/if}
						</div>
					{:else}
						<div>
							<Text element="p">
								<Text element="span" styleType="body-extra-small" class="text-bold">
									{$format('100%', {
										default: '100%',
									})}
								</Text>
								<Text element="span" styleType="body-extra-small">
									{$format('Completed', {
										default: 'Completed',
									})}
								</Text>
							</Text>
						</div>
					{/if}
				</div>
			</div>
			<!-- Play and Pause button for start and pause ai model in training period-->
			<Button
				id="ai-model-training-play-pause"
				aria-labelledby="ai-model-training-play-pause"
				disabled={progressReadinessPercent === 100 || !deviceIsOnline}
				size="m"
				width="full"
				variant="secondary"
				icon={`fas fa-${enabled ? 'pause' : 'play'}`}
				on:click={() => {
					enabled = !enabled;
					handleAITraining();
				}}
			>
				<!-- <span class="text-decoration-underline">{AIModelState} AI training</span
				> -->
				<span class="text-decoration-underline"
					>{capFirstChar(
						$format('play_or_pause_ai_model_training', {
							default: `${AIModelState} AI training`,
							values: { AIModelState: $format(AIModelState) },
						}),
					)}</span
				>
			</Button>
		</div>
	</div>
	<hr class="mt-m mb-m hr-color-2" />
	<!-- Information about ai model training required hours -->
	<div>
		<div class="d-flex align-items-start">
			<Icon icon="fas fa-asterisk" size="xs" class="text-danger mr-s" />
			<Text element="p" styleType="tiny" class="text-color-muted">
				{@html $format('ai_model_requires_training_2', {
					default: `AI model needs approximately <b>${requiredHours}</b> working hours to be fully trained.`,
					values: { requiredHours: requiredHours },
				})}
				<Text
					element="span"
					styleType="tiny"
					class="text-primary cursor-pointer"
					on:click={() => {
						showAboutAIModelModal = true;
					}}
				>
					{$format('link_learn_more', {
						default: 'Learn more',
					})}
				</Text>
			</Text>
		</div>
	</div>
	<hr class="mt-m mb-m hr-color-2" />
	<!-- Reset and Cancel AI model training buttons -->
	<div>
		<Button
			disabled={progressReadinessPercent === 100}
			size="m"
			width="full"
			variant="secondary"
			class="mb-s"
			on:click={() => {
				showResetModal = true;
				dispatch('resetCancelEraseAIModelData', 'reset');
			}}
			>{$format('reset_ai_training', {
				default: 'Reset AI training',
			})}</Button
		>
		<Button
			size="m"
			width="full"
			variant="danger-secondary"
			disabled={progressReadinessPercent === 100}
			on:click={() => {
				showResetModal = true;
				dispatch('resetCancelEraseAIModelData', 'cancel');
			}}
			>{$format('cancel_ai_training', {
				default: 'Cancel AI training',
			})}</Button
		>
	</div>
</Container>
